import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  BLANK_GAMEWEEK_COLOUR_CLASS,
  DARK_DIFFICULTY_COLOUR_CLASSES
} from './difficulty-palette';
import type { Player } from '@api-service/players';
import { ratingToDifficulty } from '@components/fixtures/v2/utils';
import { TeamBadge } from '@components/transfers/TeamBadge';
import type { GameweekWindowEntry } from '@lib/article-blocks/types';

export const POSITION_LABELS: Record<Player['position'], string> = {
  GK: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Forward'
};

const MODERATE_DIFFICULTY = 5;

// The FPL web name ("Haaland", "B.Fernandes") rather than the full legal name:
// it is what readers know the player as and it fits a card header on one line.
export const splitPlayerName = (
  player: Pick<Player, 'displayName'>
): { firstName: string; lastName?: string } => {
  const parts = player.displayName.trim().split(' ');
  const lastName = parts.pop();
  return { firstName: parts.join(' '), lastName };
};

export const TeamBadgeSlot = ({ shortName }: { shortName: string }) => (
  <span className="inline-flex h-5 w-6 shrink-0 items-center justify-center align-middle">
    <TeamBadge shortName={shortName} size="sm" className="!mb-0" />
  </span>
);

export const CardShell = ({
  children,
  testId
}: {
  children: ReactNode;
  testId: string;
}) => (
  <section
    className="not-prose my-8 rounded-3xl bg-[#2c3134] p-3 sm:p-5"
    data-cy={testId}
  >
    <div className="flex flex-col gap-4 rounded-[1.5rem] bg-[#1b1f21] p-3 sm:gap-5 sm:p-6">
      {children}
    </div>
  </section>
);

// Blue-on-dark wordmark shared with the social image cards.
const WORDMARK_SRC = '/images/logos/ffh-wordmark-blue.svg';

export const TitleBar = ({
  title,
  detail
}: {
  title: string;
  detail: string;
}) => (
  // Equal outer columns keep the title centred on the bar, not between the
  // (wider) logo and the (narrower) range label.
  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl bg-[#2c3134] px-4 py-3">
    <img
      src={WORDMARK_SRC}
      alt="Fantasy Football Hub"
      className="!mb-0 h-5 w-auto justify-self-start sm:h-6"
    />
    <span className="truncate text-center text-base font-bold leading-none text-[#1FD0FF] sm:text-xl">
      {title}
    </span>
    <span className="justify-self-end whitespace-nowrap text-sm leading-none text-[#fcfcfc] sm:text-base">
      {detail}
    </span>
  </div>
);

const Separator = () => (
  <span aria-hidden className="text-[#d9d9d9]">
    •
  </span>
);

/**
 * Headshot in a white circle, or the club badge when the player has no photo.
 * Some photo URLs are dead links, so a failed load falls back too. The cards
 * are server rendered, so a photo can fail before React attaches onError; the
 * effect catches that case.
 */
export const PlayerPhoto = ({
  player,
  sizeClass = 'h-16 w-16 sm:h-24 sm:w-24'
}: {
  player: Player;
  sizeClass?: string;
}) => {
  const [hasPhotoError, setHasPhotoError] = useState(false);
  const photoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const photo = photoRef.current;
    if (photo?.complete && photo.naturalWidth === 0) setHasPhotoError(true);
  }, [player.imageUrl]);

  const isShowingPhoto = Boolean(player.imageUrl) && !hasPhotoError;

  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white`}
    >
      {isShowingPhoto ? (
        <img
          ref={photoRef}
          src={player.imageUrl}
          alt={player.displayName}
          className="!mb-0 h-full w-full origin-bottom scale-110 object-cover"
          onError={() => setHasPhotoError(true)}
        />
      ) : (
        <img
          src={`/badges/${player.team.shortName}.png`}
          alt={player.team.fullName}
          className="!mb-0 h-3/5 w-3/5 object-contain"
          data-cy="player-photo-badge"
        />
      )}
    </div>
  );
};

export const PlayerHeader = ({ player }: { player: Player }) => {
  const { firstName, lastName } = splitPlayerName(player);
  return (
    <header className="flex items-center gap-4">
      <PlayerPhoto player={player} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 text-sm leading-none text-[#fcfcfc] sm:text-base">
          <TeamBadgeSlot shortName={player.team.shortName} />
          <span>{player.team.fullName}</span>
          <Separator />
          <span>{POSITION_LABELS[player.position]}</span>
          <Separator />
          <span>£{player.price.toFixed(1)}m</span>
        </div>
        <h3 className="mt-1 truncate pb-1 text-2xl leading-snug text-[#fcfcfc] sm:text-4xl">
          {firstName && <span>{firstName} </span>}
          <span className="font-bold">{lastName}</span>
        </h3>
      </div>
    </header>
  );
};

export const fixtureColourClass = (
  fixture: GameweekWindowEntry['fixtures'][number]
): string =>
  DARK_DIFFICULTY_COLOUR_CLASSES[
    ratingToDifficulty(
      fixture.inferences?.difficulty.overall ?? MODERATE_DIFFICULTY
    )
  ];

export const venueLabel = (
  fixture: GameweekWindowEntry['fixtures'][number]
): string => (fixture.venue === 'home' ? 'H' : 'A');

export const FixtureTile = ({
  fixtures,
  isCompact = false
}: {
  fixtures: GameweekWindowEntry['fixtures'];
  isCompact?: boolean;
}) => {
  const heightClass = isCompact ? 'min-h-[2.75rem]' : 'min-h-[5.5rem]';

  if (fixtures.length === 0) {
    return (
      <div
        className={`flex h-full ${heightClass} items-center justify-center rounded-xl px-2 text-center text-xs font-bold text-[#7f8689] ${BLANK_GAMEWEEK_COLOUR_CLASS}`}
      >
        {isCompact ? 'Blank' : 'No fixture'}
      </div>
    );
  }

  return (
    <div
      className={`flex h-full ${heightClass} flex-col gap-0.5 overflow-hidden rounded-xl`}
    >
      {fixtures.map((fixture) => (
        <div
          key={`${fixture.gameweek}-${fixture.opponent.shortName}`}
          className={`flex flex-1 items-center justify-center gap-1 py-1 text-xs font-bold text-[#faf5f5] sm:text-sm ${
            isCompact ? 'flex-row' : 'flex-col'
          } ${fixtureColourClass(fixture)}`}
        >
          {!isCompact && (
            <TeamBadge
              shortName={fixture.opponent.shortName}
              size="lg"
              className="!mb-0"
            />
          )}
          <span className="whitespace-nowrap">
            {fixture.opponent.shortName} ({venueLabel(fixture)})
          </span>
        </div>
      ))}
    </div>
  );
};

export const GameweekGrid = ({
  gameweeks,
  isCompact = false
}: {
  gameweeks: GameweekWindowEntry[];
  isCompact?: boolean;
}) => {
  const columns = Math.max(gameweeks.length, 1);
  return (
    <div
      className="grid gap-2 sm:gap-3"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {gameweeks.map(({ gameweek }) => (
        <div
          key={`label-${gameweek}`}
          className="text-center text-xs font-bold text-[#a5aaad] sm:text-sm"
        >
          GW{gameweek}
        </div>
      ))}
      {gameweeks.map(({ gameweek, fixtures }) => (
        <FixtureTile
          key={`tile-${gameweek}`}
          fixtures={fixtures}
          isCompact={isCompact}
        />
      ))}
    </div>
  );
};
